import React, { ReactNode, useId, useRef, useState, WheelEventHandler } from 'react';
import { FieldError } from 'react-hook-form';
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';

import { WithRef } from 'support/types/withRef';

import { InputLabel } from '../InputLabel/InputLabel';
import { LeadingAddOn } from '../LeadingAddOn/LeadingAddOn';
import { TrailingAddOn } from '../TrailingAddOn/TrailingAddOn';

import './Input.css';

import { useResizeObserver } from 'usehooks-ts';

import { cn } from 'support/helpers/cn/cn';
import { FieldState } from 'support/types/ui';
import { Button } from 'components/Form/Button';

const wrapperTextClassByState: Record<FieldState, string> = {
  default: '',
  error: 'text-red-700 focus-within:text-inherit',
  warning: 'text-warning-700 focus-within:text-inherit',
  'pending-validation': 'text-blue-700 focus-within:text-inherit',
};

const inputClassByState: Record<FieldState, string> = {
  default: 'focus-visible:ring-procuros-green-400 focus-visible:border-procuros-green-400',
  error:
    'bg-red-50 placeholder:text-red-700 focus-visible:ring-red-500 focus-visible:border-red-500 focus-visible:outline-hidden focus:bg-transparent',
  warning:
    'bg-warning-50 placeholder:text-warning-700 focus-visible:ring-warning-500 focus-visible:border-warning-500 focus-visible:outline-hidden focus:bg-transparent',
  'pending-validation':
    'bg-blue-50 placeholder:text-blue-700 focus-visible:ring-blue-500 focus-visible:border-blue-500 focus-visible:outline-hidden focus:bg-transparent',
};

const trailingIconByState: Record<FieldState, ReactNode> = {
  default: null,
  error: <ExclamationCircleIcon className="size-5 text-red-500" aria-hidden="true" />,
  warning: <ExclamationTriangleIcon className="size-5 text-warning-500" aria-hidden="true" />,
  'pending-validation': <MinusCircleIcon className="size-5 text-blue-500" aria-hidden="true" />,
};

type NativeInputProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  | 'autoComplete'
  | 'className'
  | 'defaultValue'
  | 'disabled'
  | 'id'
  | 'max'
  | 'min'
  | 'name'
  | 'onClick'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'placeholder'
  | 'readOnly'
  | 'required'
  | 'step'
  | 'type'
  | 'minLength'
  | 'maxLength'
  | 'onKeyDown'
  | 'title'
>;

type InputVariantProps =
  | { variant?: 'default'; valueDescription?: never }
  | { variant: 'large'; valueDescription?: string };

type InputProps = InputVariantProps & {
  description?: ReactNode;
  errors?: FieldError;
  fieldState?: FieldState;
  label?: string;
  leftAddon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightAddon?: React.ReactNode;
  value?: React.InputHTMLAttributes<HTMLInputElement>['value'] | null;
  inputWrapperClassName?: string;
  wrapperClassName?: string;
  wrapperRef?: React.Ref<HTMLDivElement>;
} & NativeInputProps;

const Input = ({
  variant,
  valueDescription,
  label,
  leftIcon,
  leftAddon,
  rightAddon,
  className,
  inputWrapperClassName,
  wrapperClassName,
  errors,
  fieldState,
  disabled,
  required,
  description,
  type = 'text',
  id,
  wrapperRef,
  ref,
  ...otherProps
}: WithRef<InputProps, HTMLInputElement>) => {
  const inputId = useId();
  const { isPasswordVisible, toggleIsPasswordVisible } = usePasswordVisibility();
  const showPasswordVisibilityButton = type === 'password';
  const inputValue = getInputValue(otherProps.value);
  const resolvedState = getResolvedState(fieldState, errors);

  const leftAddonRef = useRef<HTMLDivElement>(null);
  const { width: leftAddonWidth } = useResizeObserver({
    ref: leftAddonRef as React.RefObject<HTMLDivElement>,
    box: 'border-box',
  });

  const rightAddonRef = useRef<HTMLDivElement>(null);
  const { width: rightAddonWidth } = useResizeObserver({
    ref: rightAddonRef as React.RefObject<HTMLDivElement>,
    box: 'border-box',
  });

  const isLarge = variant === 'large';

  return (
    <div className={cn(wrapperClassName, wrapperTextClassByState[resolvedState])} ref={wrapperRef}>
      {/* Label + Description container when large */}
      {(label || description) && isLarge ? (
        <div className="mb-2 flex flex-col gap-1">
          <InputLabel label={label} variant={variant} required={required} />
          {description && <p className="text-sm text-gray-700">{description}</p>}
        </div>
      ) : (
        <InputLabel label={label} variant={variant} required={required} htmlFor={id ?? inputId} />
      )}

      {/* Value Description + Input container */}
      {isLarge && valueDescription && <p className="mb-1 text-sm text-gray-500">{valueDescription}</p>}

      <div className={cn('group relative', !isLarge && label ? 'mt-1' : undefined, inputWrapperClassName)}>
        <LeadingAddOn show={!!leftIcon} leadingAddOn={leftIcon} state={resolvedState} />
        <LeadingAddOn show={!!leftAddon} leadingAddOn={leftAddon} state={resolvedState} ref={leftAddonRef} />
        <input
          ref={ref}
          type={getInputType(type, isPasswordVisible)}
          className={cn(
            'box-border block w-full rounded-md text-sm shadow-xs read-only:cursor-not-allowed focus-visible:outline-hidden disabled:cursor-not-allowed',
            'border-gray-300',
            inputClassByState[resolvedState],
            {
              'pl-9': !leftAddon && leftIcon,
              'input--date': type === 'date',
              'data-hj-allow': type !== 'password',
              'disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 overflow-hidden flex items-center relative flex-1 min-w-0 bg-white border shadow-xs text-left focus-visible:outline-hidden active:outline-hidden active:ring-0 focus-visible:ring-0':
                disabled,
            },
            className,
          )}
          style={{
            paddingLeft: calculatePaddingForAddons(leftAddonWidth),
            paddingRight: type !== 'checkbox' ? calculatePaddingForAddons(rightAddonWidth) : undefined,
          }}
          aria-label={label ? undefined : otherProps.name}
          disabled={disabled}
          data-testid={id}
          id={id ?? inputId}
          {...otherProps}
          value={inputValue}
          onWheel={numberInputOnWheelPreventChange}
        />
        <TrailingAddOn
          state={resolvedState}
          ref={rightAddonRef}
          show={resolvedState !== 'default' || Boolean(rightAddon) || showPasswordVisibilityButton}
          disablePointerEvents={!showPasswordVisibilityButton}
          trailingAddOn={
            <div className="flex items-center gap-2">
              {rightAddon}
              <PasswordVisibilityButton
                show={showPasswordVisibilityButton}
                isPasswordVisible={isPasswordVisible}
                toggleIsPasswordVisible={toggleIsPasswordVisible}
              />
              {trailingIconByState[resolvedState]}
            </div>
          }
        />
      </div>

      {!!errors && <span className="mt-2 text-sm text-red-600">{errors.message}</span>}
      {!!description && !isLarge && <p className="mt-2 text-sm text-gray-500">{description}</p>}
    </div>
  );
};

export { Input };

const PasswordVisibilityButton = ({
  show,
  isPasswordVisible,
  toggleIsPasswordVisible,
}: {
  show: boolean;
  isPasswordVisible: boolean;
  toggleIsPasswordVisible: () => void;
}) =>
  show ? (
    <Button
      variant="minimal"
      size="extra-small"
      iconOnly
      onClick={toggleIsPasswordVisible}
      LeftIcon={isPasswordVisible ? EyeIcon : EyeSlashIcon}
      analyticsId={''}
    />
  ) : null;

const getInputValue = (value: InputProps['value']): Exclude<InputProps['value'], null> => {
  if (typeof value === 'number') {
    return isNaN(value) ? '' : value;
  }
  return value === null ? '' : value;
};

const getResolvedState = (fieldState: FieldState | undefined, errors: FieldError | undefined): FieldState =>
  fieldState ?? (errors ? 'error' : 'default');

const getInputType = (type: NativeInputProps['type'], isPasswordVisible: boolean) =>
  type === 'password' && isPasswordVisible ? 'text' : type;

const numberInputOnWheelPreventChange: WheelEventHandler<HTMLInputElement> = (e) => {
  const target = e.target as HTMLInputElement;
  // Prevent the input value change
  target.blur();

  // Prevent the page/container scrolling
  e.stopPropagation();
};

const usePasswordVisibility = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const toggleIsPasswordVisible = () => {
    setIsPasswordVisible((visible) => !visible);
  };

  return { isPasswordVisible, toggleIsPasswordVisible };
};

const calculatePaddingForAddons = (addonWidth: number | undefined) => {
  return addonWidth ? `calc(${addonWidth}px + 0.2rem)` : undefined;
};
